import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnChanges,
    OnInit,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    HeroSectionData,
    FeaturedCollectionData,
    MarketingBannerData,
    ExternalHtmlContentData,
    KeyPointListData,
    PageComponentData,
    CollectionGroupData,
    EmailSubscriptionData,
    ProductListingData,
    CollectionCarouselData,
    AboutFormData,
    ContactFormData,
    ProductReviewsData,
    ProductInfoData,
    TopReviewsData,
    CardCarouselBanner,
    FaqListData,
    SimpleProductInfoData,
    PageComponentTitleMode,
    TitleInfo
} from '../../../common/interfaces';
import { CarouselArrowType } from '../../../common/enums';
import { Product, ProductVariant } from '../../../common/generated-types';

@Component({
    selector: 'vsf-page-component',
    templateUrl: './page-component.component.html',
    styleUrls: ['./page-component.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageComponent implements OnInit, OnChanges, AfterViewInit {
    @Input() pageComponent: PageComponentData;
    @Input() isMobile: boolean | null;
    @Input() linePadding: string;
    //TODO: apply this to all components, currently only applied to key-point-list and marketing-banner component
    @Input() sectionRoundedClass: string = '';
    @Input() horizontalPadding: number;
    @Input() sellerChannelId: string;
    @Input() pdpAssetsArrowType: CarouselArrowType;
    @Input() pageFullWidth: boolean = false;
    @Input() sectionMaxHeight: string = '';
    @Input() isPageHeading: boolean = false;
    @Input() h1StyleMode: string = 'auto';
    constructor(
                private router: Router,
                private route: ActivatedRoute,
                private changeDetector: ChangeDetectorRef,
                ) {}

    type: string;
    marketingBanner: MarketingBannerData | undefined;
    keyPointList: KeyPointListData | undefined;
    cardCarouselBanner: CardCarouselBanner | undefined;
    heroSection: HeroSectionData | undefined;
    externalHtmlContent: ExternalHtmlContentData | undefined;
    faqList: FaqListData | undefined;
    featuredCollection: FeaturedCollectionData | undefined;
    collectionGroup: CollectionGroupData | undefined;
    topReviews: TopReviewsData | undefined;
    emailSubscription: EmailSubscriptionData | undefined;
    productInfo: ProductInfoData | undefined;
    simpleProductInfo: SimpleProductInfoData | undefined;
    productListing: ProductListingData | undefined;
    productReviews: ProductReviewsData | undefined;
    collectionCarousel: CollectionCarouselData | undefined;
    aboutForm: AboutFormData | undefined;
    contactForm: ContactFormData | undefined;
    isBrowser: boolean;
    titleInfo: TitleInfo;
    productInfoProduct: Product | null = null;
    productInfoVariant: ProductVariant | null = null;
    isFullBleedComponent: boolean = false;

    private static readonly FULL_BLEED_TYPES = new Set([
        'marketing-banner', 'hero-section', 'email-subscription',
    ]);

    ngOnInit(): void {
        const pageComponent = this.pageComponent;
        if(pageComponent) {
            this.type = pageComponent.type;
            this.isFullBleedComponent = PageComponent.FULL_BLEED_TYPES.has(this.type);
            this.titleInfo = {
                title: pageComponent.title || '',
                subtitle: pageComponent.subtitle || '',
                titleUnderline: pageComponent.titleUnderline === true,
                titleMode: pageComponent.titleMode as PageComponentTitleMode
                    || (pageComponent.title || pageComponent.subtitle) ? 'for-section' : 'disabled',
                isPageHeading: this.isPageHeading,
                h1StyleMode: this.h1StyleMode,
            };
            switch(pageComponent.type) {
                case 'marketing-banner':
                    this.marketingBanner = pageComponent.content as MarketingBannerData;
                    break;
                case 'key-point-list':
                    this.keyPointList = pageComponent.content as KeyPointListData;
                    break;
                case 'hero-section':
                    this.heroSection = pageComponent.content as HeroSectionData;
                    break;
                case 'external-html-content':
                    this.externalHtmlContent = pageComponent.content as ExternalHtmlContentData;
                    break;
                case 'faq-list':
                    this.faqList = pageComponent.content as FaqListData;
                    break;
                case 'featured-collection':
                    this.featuredCollection = pageComponent.content as FeaturedCollectionData;
                    break;
                case 'collection-group':
                    this.collectionGroup = pageComponent.content as CollectionGroupData;
                    break;
                case 'top-reviews':
                    this.topReviews = pageComponent.content as TopReviewsData;
                    break;
                case 'product-info-section':
                    this.productInfo = pageComponent.content as ProductInfoData;
                    this.resolveProductInfoData();
                    break;
                case 'simple-product-info-section':
                    this.simpleProductInfo = pageComponent.content as SimpleProductInfoData;
                    break;
                case 'email-subscription':
                    this.emailSubscription = pageComponent.content as EmailSubscriptionData;
                    break;
                case 'product-listing-section':
                    this.productListing = pageComponent.content as ProductListingData;
                    if (this.productListing?.productListingType === 'facetValue' &&
                        this.productListing?.facetValues &&
                        this.productListing?.facetValues.length > 0) {
                        // Group facet values by their facet id
                        const facetGroupMap = this.productListing.facetValues.reduce((acc, facetValue) => {
                            const facetId = facetValue.facet.id;
                            if (!acc[facetId]) {
                                acc[facetId] = [];
                            }
                            acc[facetId].push(facetValue.id);
                            return acc;
                        }, {} as { [facetId: string]: string[] });

                        // Build the query string: facetId1:val1,val2;facetId2:val3,val4
                        const facetQuery = Object.keys(facetGroupMap)
                            .map(facetId => `${facetId}:${facetGroupMap[facetId].join(',')}`)
                            .join(';');

                        // Update the URL to include the facets query param.
                        // This will trigger the product list component to pick up these facet filters.
                        this.router.navigate([], {
                            relativeTo: this.route,
                            queryParams: { facets: facetQuery },
                            queryParamsHandling: 'merge'
                        });
                    }
                    break;
                case 'card-carousel-banner':
                    this.cardCarouselBanner = pageComponent.content as CardCarouselBanner;
                    break;
                case 'collection-carousel':
                    this.collectionCarousel = pageComponent.content as CollectionCarouselData;
                    break;
                case 'about-form':
                    this.aboutForm = pageComponent.content as AboutFormData;
                    break;
                case 'contact-form':
                    this.contactForm = pageComponent.content as ContactFormData;
                    break;
                case 'product-reviews':
                    this.productReviews = pageComponent.content as ProductReviewsData;
                    break;
                default:
                    break;
            }
        }
        this.changeDetector.markForCheck();
    }


    private getDefaultVariant(product: Product | null | undefined): ProductVariant | null {
        if (!product?.variants?.length) {
            return null;
        }
        const firstVariant = product.variants[0] as ProductVariant;
        const defaultVariantId = product.customFields?.defaultVariant?.id;
        if (!defaultVariantId) {
            return firstVariant;
        }
        return (product.variants.find((variant: any) => variant.id === defaultVariantId) as ProductVariant) || firstVariant;
    }

    private resolveProductInfoData() {
        const variantFromContent = this.productInfo?.productVariant || null;
        const productFromContent = this.productInfo?.product || null;
        this.productInfoProduct = productFromContent || variantFromContent?.product || null;
        this.productInfoVariant = variantFromContent || this.getDefaultVariant(this.productInfoProduct);
    }

    ngOnChanges() {

    }

    ngAfterViewInit() {
        if(!this.isBrowser) {
            return;
        }
    }
}
